import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AppProvider, useApp } from './context/AppContext';
import { Toaster } from './components/ui/sonner';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { WhatsAppButton } from './components/WhatsAppButton';
import { CookieBanner } from './components/CookieBanner';
import { MaintenancePage } from './components/MaintenancePage';
import { SEOHead } from './components/SEOHead';
import { ThemeProvider } from './components/ThemeProvider';
import HomePage from './pages/HomePage';
import ApartmentsPage from './pages/ApartmentsPage';
import ApartmentDetailPage from './pages/ApartmentDetailPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import PoliciesPage from './pages/PoliciesPage';
import UtilityIndexPage from './pages/UtilityIndexPage';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminApartmentsPage from './pages/AdminApartmentsPage';
import AdminCategoriesPage from './pages/AdminCategoriesPage';
import AdminNewsletterPage from './pages/AdminNewsletterPage';
import AdminSettingsPage from './pages/AdminSettingsPage';
import AdminAboutPage from './pages/AdminAboutPage';
import AdminPoliciesPage from './pages/AdminPoliciesPage';
import AdminContactSettingsPage from './pages/AdminContactSettingsPage';
import AdminMessagesPage from './pages/AdminMessagesPage';
import AdminEmailSettingsPage from './pages/AdminEmailSettingsPage';
import AdminCollaboratorsPage from './pages/AdminCollaboratorsPage';
import AdminWorkTrackingPage from './pages/AdminWorkTrackingPage';
import AdminInvoicesPage from './pages/AdminInvoicesPage';
import AdminBackupPage from './pages/AdminBackupPage';
import AdminContractsPage from './pages/AdminContractsPage';
import AdminSEOPage from './pages/AdminSEOPage';
import AdminDeploymentPage from './pages/AdminDeploymentPage';
import AdminServicePage from './pages/AdminServicePage';
import AdminUtilityIndexPage from './pages/AdminUtilityIndexPage';
import AdminDriverCalculatorPage from './pages/AdminDriverCalculatorPage';
import ServiceSheetVerifyPage from './pages/ServiceSheetVerifyPage';
import QuickPunchPage from './pages/QuickPunchPage';
import axios from 'axios';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Component to conditionally render WhatsApp button
function ConditionalWhatsApp() {
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith('/admin');
  
  if (isAdminPage) return null;
  return <WhatsAppButton />;
}

// Main content component that checks maintenance mode
function MainContent() {
  const location = useLocation();
  const { language } = useApp();
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState({});
  const [loading, setLoading] = useState(true);
  
  const isAdminPage = location.pathname.startsWith('/admin');
  
  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/maintenance-status`);
        setMaintenanceMode(data.maintenance_mode);
        setMaintenanceMessage(data.maintenance_message);
      } catch (error) {
        console.error('Failed to check maintenance status');
      } finally {
        setLoading(false);
      }
    };
    
    checkMaintenance();
    // Check every 30 seconds in case admin toggles maintenance
    const interval = setInterval(checkMaintenance, 30000);
    return () => clearInterval(interval);
  }, []);
  
  // Show maintenance page for non-admin pages
  if (!loading && maintenanceMode && !isAdminPage) {
    return <MaintenancePage message={maintenanceMessage} language={language} />;
  }
  
  // Check if we're on punch page to hide header/footer
  const isPunchPage = location.pathname === '/punch' || location.pathname === '/quick-punch';
  
  // Check if we're on service sheet verification page (standalone, no header/footer)
  const isVerifyPage = location.pathname.startsWith('/service-sheet/') || location.pathname.startsWith('/v/');
  
  // For verify page, render without any site chrome
  if (isVerifyPage) {
    return (
      <Routes>
        <Route path="/v/:code" element={<ServiceSheetVerifyPage />} />
        <Route path="/service-sheet/:sheetId" element={<ServiceSheetVerifyPage />} />
      </Routes>
    );
  }
  
  return (
    <>
      <SEOHead />
      {!isPunchPage && <Header />}
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/apartments" element={<ApartmentsPage />} />
        <Route path="/apartments/:id" element={<ApartmentDetailPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/utility-index" element={<UtilityIndexPage />} />
        <Route path="/policies" element={<PoliciesPage />} />
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin" element={<AdminDashboardPage />} />
        <Route path="/admin/apartments" element={<AdminApartmentsPage />} />
        <Route path="/admin/categories" element={<AdminCategoriesPage />} />
        <Route path="/admin/newsletter" element={<AdminNewsletterPage />} />
        <Route path="/admin/settings" element={<AdminSettingsPage />} />
        <Route path="/admin/about" element={<AdminAboutPage />} />
        <Route path="/admin/policies" element={<AdminPoliciesPage />} />
        <Route path="/admin/contact-settings" element={<AdminContactSettingsPage />} />
        <Route path="/admin/messages" element={<AdminMessagesPage />} />
        <Route path="/admin/utility-index" element={<AdminUtilityIndexPage />} />
        <Route path="/admin/email-settings" element={<AdminEmailSettingsPage />} />
        <Route path="/admin/collaborators" element={<AdminCollaboratorsPage />} />
        <Route path="/admin/work-tracking" element={<AdminWorkTrackingPage />} />
        <Route path="/admin/invoices" element={<AdminInvoicesPage />} />
        <Route path="/admin/backup" element={<AdminBackupPage />} />
        <Route path="/admin/contracts" element={<AdminContractsPage />} />
        <Route path="/admin/seo" element={<AdminSEOPage />} />
        <Route path="/admin/deployment" element={<AdminDeploymentPage />} />
        <Route path="/admin/service" element={<AdminServicePage />} />
        <Route path="/admin/driver-calculator" element={<AdminDriverCalculatorPage />} />
        <Route path="/v/:code" element={<ServiceSheetVerifyPage />} />
        <Route path="/service-sheet/:sheetId" element={<ServiceSheetVerifyPage />} />
        <Route path="/punch" element={<QuickPunchPage />} />
        <Route path="/quick-punch" element={<QuickPunchPage />} />
      </Routes>
      {!isPunchPage && <Footer />}
      {!isPunchPage && <ConditionalWhatsApp />}
      {!isPunchPage && <CookieBanner />}
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AppProvider>
        <ThemeProvider>
          <BrowserRouter>
            <div className="App">
              <MainContent />
              <Toaster position="top-right" richColors />
            </div>
          </BrowserRouter>
        </ThemeProvider>
      </AppProvider>
    </HelmetProvider>
  );
}

export default App;